import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';
import { ThemeService } from '../../core/services/theme.service';
import { RegionService } from '../../core/services/region.service';
import { THEME_GROUP_OPTIONS } from '../../core/constants/theme-options';
import type { RegionId } from '../../core/constants/regions';

/**
 * im-desktop-welcome — the desktop-only first-run welcome + region picker.
 *
 * RESPONSIBILITY: The very first surface a desktop user sees (it has no web
 * equivalent). It captures appearance (theme) and — critically — the **data
 * region** before the app makes ANY backend call (data residency). Rendered by
 * AppComponent while `regionGateActive()` holds; once a region is picked the
 * app reloads region-aware and boots normally.
 *
 * AI CONTEXT:
 *   - Network-silent by design: this screen must not call any backend. It only
 *     touches ThemeService (local DOM) and RegionService (localStorage).
 *   - All visuals use Ricoh Paper tokens (no hex) + an SVG backdrop that
 *     respects `prefers-reduced-motion`.
 */
@Component({
  selector: 'im-desktop-welcome',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperIconComponent],
  templateUrl: './desktop-welcome.component.html',
  styleUrl: './desktop-welcome.component.scss',
})
export class DesktopWelcomeComponent {
  private readonly theme = inject(ThemeService);
  private readonly region = inject(RegionService);

  /** Wizard step: 0 hero · 1 appearance · 2 region. */
  protected readonly step = signal<0 | 1 | 2>(0);

  /** Step indices for the progress dots (literal tuple so goTo() stays typed). */
  protected readonly steps = [0, 1, 2] as const;

  /** Theme tiles (light + dark groups), reused from the appearance page source. */
  protected readonly themeOptions = THEME_GROUP_OPTIONS;

  /** Selectable data regions. */
  protected readonly regions = this.region.regions;

  /** Currently active theme (for tile highlight) — live-applies on click. */
  protected readonly activeTheme = this.theme.theme;

  /** A region selection is committing (prevents double-clicks before reload). */
  protected readonly committing = signal(false);

  protected next(): void {
    this.step.update((s) => (s < 2 ? ((s + 1) as 0 | 1 | 2) : s));
  }

  protected back(): void {
    this.step.update((s) => (s > 0 ? ((s - 1) as 0 | 1 | 2) : s));
  }

  protected goTo(step: 0 | 1 | 2): void {
    this.step.set(step);
  }

  protected pickTheme(name: (typeof THEME_GROUP_OPTIONS)[number]['name']): void {
    this.theme.setTheme(name);
  }

  /**
   * Commit the chosen region and reload so guards + Apollo re-resolve against
   * the new region from a clean, region-aware boot. (MVP: a reload is the
   * simplest correct restart; a no-reload path is a fast-follow.)
   */
  protected chooseRegion(id: RegionId): void {
    if (this.committing()) return;
    this.committing.set(true);
    this.region.select(id);
    // Defer to next tick so the "committing" UI state paints first.
    setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        this.committing.set(false);
      }
    }, 150);
  }
}
